// Axis-label placement for the numeric sliders (ACT2-531, ACT2-534).
//
// ONE row: the data's start and end anchor the two edges, and as many landmarks as genuinely fit go
// between them — added in order of usefulness, not left-to-right. So a crowded axis keeps the labels
// that carry the most information rather than whichever happened to sort first, and a roomy axis shows
// every one of them.
//
// Fit is computed against the MEASURED axis width rather than fixed percentage gaps, because "if it
// fits" is a question about pixels and characters, not about percent: the same two labels collide at
// 320px and sit comfortably apart at 640px. Text is measured by character count against the mono
// advance width — exact enough for a monospaced face, and free.

/**
 * Priority order for the landmark labels: the most informative first. Anything unlisted goes last.
 *
 * The keys are the plain-English names a marketing reader can act on, NOT percentile jargon (ACT2-569):
 * `p25` is a statistics term, and the people reading this control are building an audience, not a
 * report. Keep any new key a phrase, not a symbol.
 */
const RANK: Record<string, number> = {
  Median: 0,
  'Top 5%': 1,
  'Top 1%': 2,
  'Top 25%': 3,
  'Bottom 5%': 4,
  'Bottom 25%': 5,
  'Bottom 1%': 6,
};
const UNRANKED = 7;

export interface AxisLabel {
  /** Position along the axis, in percent. */
  pos: number;
  /** The landmark's name — `Median`, `Top 5%`, … — shown under its value. */
  label: string;
  /** The formatted value, shown above the name. */
  value: string;
}

/** An axis end: its formatted value, plus the name of what that end IS when it can be proven —
 *  `Lowest` when the frame reaches the column's smallest value, `Bottom 1%` when it was cut there
 *  instead. Unnamed when neither can be shown to hold, because an end labelled with the wrong
 *  percentile is the exact defect this module exists to prevent. */
export interface AxisEnd {
  value: string;
  name?: string;
}

export interface AxisMetrics {
  /** Measured width of the scale row, in px. `0` (unmeasured) ⇒ only the end anchors are placed. */
  width: number;
  /** Advance width of one character on the VALUE line, in px. */
  valueChar: number;
  /** Advance width of one character on the NAME line, in px. */
  labelChar: number;
  /** Clear space required between two neighbouring labels, in px. */
  gap: number;
}

/** The px width a stacked value-over-name label occupies — whichever of its two lines is wider. */
function labelWidth(mark: Pick<AxisLabel, 'label' | 'value'>, m: AxisMetrics): number {
  return Math.max(mark.value.length * m.valueChar, mark.label.length * m.labelChar);
}

/** Same, for an end anchor, whose name line is optional. */
function endWidth(end: AxisEnd, m: AxisMetrics): number {
  return Math.max(end.value.length * m.valueChar, (end.name?.length ?? 0) * m.labelChar);
}

type Slot = [number, number];

const overlaps = (a: Slot, b: Slot) => a[0] < b[1] && b[0] < a[1];

/**
 * Which landmarks fit on the axis beside the two end anchors, in the order a reader benefits from most.
 *
 * The ends are placed first and never yield: they are the scale itself, and a landmark that displaced
 * one would leave the axis without a range. Everything else is offered in `RANK` order and kept only if
 * it clears what is already down — rank alone decides, with no landmark treated as a prerequisite for
 * any other. Because `Median` ranks first, it drops out only when it genuinely cannot be drawn.
 */
export function placeAxisLabels(
  candidates: AxisLabel[],
  ends: { min: AxisEnd; max: AxisEnd },
  metrics: AxisMetrics,
): AxisLabel[] {
  if (!(metrics.width > 0)) return [];

  const toPct = (px: number) => (px / metrics.width) * 100;
  // HALF the gap on each side of every slot, so two neighbours end up exactly `gap` apart. Padding each
  // slot by the full gap enforced double the documented clearance, which was enough to evict a median
  // sitting at 10% of the axis for a three-character end name it never actually touched.
  const gapPct = toPct(metrics.gap) / 2;
  // The ends are flush left and flush right, so they occupy a slot from their own edge inward.
  const minPct = toPct(endWidth(ends.min, metrics));
  const maxPct = toPct(endWidth(ends.max, metrics));
  const taken: Slot[] = [
    [0 - gapPct, minPct + gapPct],
    [100 - maxPct - gapPct, 100 + gapPct],
  ];

  const byRank = [...candidates].sort(
    (a, b) => (RANK[a.label] ?? UNRANKED) - (RANK[b.label] ?? UNRANKED) || a.pos - b.pos,
  );

  const kept: AxisLabel[] = [];
  for (const mark of byRank) {
    const half = toPct(labelWidth(mark, metrics)) / 2;
    const slot: Slot = [mark.pos - half - gapPct, mark.pos + half + gapPct];
    // Must sit fully on the track: a centred label straying past either edge is clipped, not just tight.
    if (slot[0] < 0 || slot[1] > 100) continue;
    if (taken.some((other) => overlaps(slot, other))) continue;
    taken.push(slot);
    kept.push(mark);
  }

  return kept.sort((a, b) => a.pos - b.pos);
}
